import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { SelectData } from '../../models';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements OnInit, OnDestroy {
  readonly FILTER_INPUT_SHOW_CONDITION_SIZE = 10;

  readonly label: InputSignal<string> = input<string>();
  readonly multiple: InputSignal<boolean> = input<boolean>();
  readonly formControlRef: InputSignal<FormControl> = input<FormControl>();
  readonly items: InputSignal<SelectData[]> = input<SelectData[]>();
  readonly required: InputSignal<boolean> = input<boolean>();
  readonly hint: InputSignal<string> = input<string>();
  readonly errorMessage: InputSignal<string> = input<string>();
  readonly id: InputSignal<string> = input<string>();

  private readonly cdr = inject(ChangeDetectorRef);

  @Output()
  selectionChange: EventEmitter<SelectData[]> = new EventEmitter();

  selectItems: SelectData[] = [];
  filteredItems: SelectData[] = [];

  appearance: MatFormFieldAppearance = 'outline';

  filterControl: FormControl<string> = new FormControl(null);

  private destroySubject: Subject<boolean> = new Subject();

  constructor() {
    effect(() => {
      this.selectItems = this.items();
      this.filteredItems = this.items();
      this.cdr.markForCheck();
    });
  }
  ngOnDestroy(): void {
    this.destroySubject.next(true);
    this.destroySubject.complete();
  }

  ngOnInit(): void {
    this.formControlRef()
      .valueChanges.pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.filterControl.reset();
        this.cdr.markForCheck();
      });
    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroySubject))
      .subscribe((value: string) => {
        if (!value) {
          this.filteredItems = this.selectItems;
          return;
        }
        // .trim() remove espaços antes e depois do termo pesquisado (evita problemas ao digitar rápido).
        const normalizedFilter = this.removeAccents(
          value.trim().toLocaleLowerCase()
        );

        const filteredValues = this.selectItems?.filter((item: SelectData) => {
          const filterWithoutAccents = this.removeAccents(
            item.label.toLocaleLowerCase()
          );
          return filterWithoutAccents.includes(normalizedFilter);
        });
        if (
          JSON.stringify(this.filteredItems) !== JSON.stringify(filteredValues)
        ) {
          this.filteredItems = filteredValues;
        }
      });
  }

  private removeAccents(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  toogleAll(): void {
    this.formControlRef().setValue(
      this.formControlRef().value?.length === this.filteredItems.length
        ? []
        : this.filteredItems
    );
  }

  compareObjects(object1: SelectData, object2: SelectData): boolean {
    return object1?.value === object2?.value;
  }
}
